import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PickList, type PickListChangeEvent } from 'primereact/picklist'

/** Çift-liste atama öğesi (id + görünen etiket). */
export interface AssignmentItem {
  id: number
  label: string
}

interface Props {
  /** Tüm atanabilir (aktif) öğeler. */
  items: AssignmentItem[]
  /** Seçili (atanmış) id'ler. */
  value: number[]
  /** Hedef liste değişince seçili id kümesi. */
  onChange: (ids: number[]) => void
  /** Sol (atanabilir) liste başlığı. */
  sourceHeader: string
  /** Sağ (atanmış) liste başlığı. */
  targetHeader: string
  /** Arama kutusu yer tutucusu. */
  filterPlaceholder?: string
}

/**
 * Paylaşılan çift-liste atama bileşeni (Reuse Mandate) — PrimeReact `PickList` sarmalayıcı.
 * id-tabanlı sözleşme: dışarıya `items` + `value` (seçili id'ler) + `onChange(ids)` sunar; içeride
 * kaynak (atanmamış) / hedef (atanmış) listelerine böler. Küme semantiği: sıra anlamsız olduğundan
 * yeniden-sıralama kontrolleri gizli, öğeler ada göre sıralı. Arama Türkçe-farkında (`filterLocale`).
 * Rol→yetki ve kullanıcı→rol atama diyalogları bunu kullanır.
 */
export default function AssignmentPickList({
  items,
  value,
  onChange,
  sourceHeader,
  targetHeader,
  filterPlaceholder,
}: Readonly<Props>) {
  const { i18n } = useTranslation()
  const filterLocale = i18n.language.startsWith('en') ? 'en' : 'tr'

  const { source, target } = useMemo(() => {
    const selected = new Set(value)
    const byLabel = (a: AssignmentItem, b: AssignmentItem) => a.label.localeCompare(b.label, filterLocale)
    return {
      source: items.filter((i) => !selected.has(i.id)).sort(byLabel),
      target: items.filter((i) => selected.has(i.id)).sort(byLabel),
    }
  }, [items, value, filterLocale])

  const handleChange = (e: PickListChangeEvent) => {
    const targetIds = (e.target as AssignmentItem[]).map((i) => i.id)
    // `items` içinde olmayan (ör. pasifleştirilmiş ama hâlâ atanmış) seçili id'ler listelerde görünmez;
    // taşıma sırasında kaybolmamaları için korunur (veri kaybı önlemi).
    const known = new Set(items.map((i) => i.id))
    const preserved = value.filter((id) => !known.has(id))
    onChange([...targetIds, ...preserved])
  }

  return (
    <PickList
      className="ui-picklist"
      dataKey="id"
      source={source}
      target={target}
      onChange={handleChange}
      itemTemplate={(item: AssignmentItem) => <span className="ui-picklist-item">{item.label}</span>}
      sourceHeader={sourceHeader}
      targetHeader={targetHeader}
      filter
      filterBy="label"
      filterLocale={filterLocale}
      sourceFilterPlaceholder={filterPlaceholder}
      targetFilterPlaceholder={filterPlaceholder}
      showSourceControls={false}
      showTargetControls={false}
    />
  )
}
